import httpClient from './httpClient';

export interface ReportingPeriodInfo {
  reporting_period_type: string;
  reporting_period_value: number;
  reporting_year: number;
  reporting_period_label: string;
  due_date: string;
}

export interface SubmissionStatus {
  state: string;
  updated_at?: string | null;
  updated_by?: string | null;
  notes?: string | null;
}

export interface SubawardeeReportRow {
  report_id: string;
  subawardee: string;
  submission_status: SubmissionStatus;
  submitted_date: string | null;
  rejected_date: string | null;
  days_until_auto_approval: number | null;
}

export interface OutstandingReportBlock {
  component_project_id: string;
  reporting_period_type: string;
  reporting_period_value: number;
  reporting_year: number;
  reporting_period_label: string;
  subawardee_reports: SubawardeeReportRow[];
}

export interface OutstandingMilestone {
  id: string;
  report_id: string;
  title: string;
  description: string | null;
  status: string;
  due_date: string;
  subawardee: string;
  activity_id?: string;
  goal_id?: string;
  key_activity_id?: string;
  objective_id?: string;
  component_project_id?: string;
}

export interface OutstandingBucket {
  component_project_id: string;
  reporting_period: ReportingPeriodInfo;
  reports: OutstandingReportBlock[];
  milestones: OutstandingMilestone[];
}

export interface PaginationInfo {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface OutstandingItemsResponse {
  items: OutstandingBucket[];
  pagination: PaginationInfo;
  filters_applied: Record<string, unknown>;
}

export type OutstandingItemsSort =
  | 'dueDate-asc'
  | 'dueDate-desc'
  | 'component-asc'
  | 'component-desc';

export interface GetOutstandingItemsParams {
  page?: number;
  page_size?: number;
  sort_by?: OutstandingItemsSort;
  reporting_year?: number;
  reporting_period_value?: number;
  reporting_period_type?: string;
  subawardee?: string;
  /** Comma-separated; each segment must match a bucket `component_project_id` exactly (after server split/trim). */
  component_project?: string;
}

/**
 * Outstanding reports/milestones buckets for the admin dashboard.
 * Uses x-admin-scope: all so staff see all mapped subawardees without x-selected-group.
 */
export async function getOutstandingItems(
  params: GetOutstandingItemsParams = {}
): Promise<OutstandingItemsResponse> {
  const response = await httpClient.get<OutstandingItemsResponse>('/v1/admin/outstanding-items', {
    params: {
      page: params.page ?? 1,
      page_size: params.page_size ?? 25,
      sort_by: params.sort_by ?? 'dueDate-asc',
      ...(params.reporting_year !== undefined && { reporting_year: params.reporting_year }),
      ...(params.reporting_period_value !== undefined && {
        reporting_period_value: params.reporting_period_value,
      }),
      ...(params.reporting_period_type && { reporting_period_type: params.reporting_period_type }),
      ...(params.subawardee && { subawardee: params.subawardee }),
      ...(params.component_project && { component_project: params.component_project }),
    },
    headers: {
      'X-Admin-Scope': 'all',
    },
  });
  return response.data;
}
